import { z } from "zod";
import {
  OPPORTUNITY_STATUS_VALUES,
  type OpportunityStatus,
} from "../schemas/opportunities";
import {
  ERPSyncConfigSchema,
  OpportunityCustomFieldMappingSchema,
  OpportunityFieldMappingSchema,
} from "./schemas";
import {
  HubSpotDealPropertyMetadataSchema,
  type HubSpotDealPropertyMetadata,
} from "./hubspotDealProperties";

export { HubSpotDealPropertyMetadataSchema };
export type { HubSpotDealPropertyMetadata };

const statusMappingShape = OPPORTUNITY_STATUS_VALUES.reduce(
  (acc, status) => ({ ...acc, [status]: z.string().min(1) }),
  {} as Record<OpportunityStatus, z.ZodString>,
);

/**
 * Per-customer HubSpot sync configuration. Stored as `integrationConfig` jsonb
 * on the `erpIntegrationSyncsTable` row. Extends the shared {@link ERPSyncConfigSchema}
 * (`syncCompanies`/`syncPeople` toggles consumed by `pullCompanies`/`pullPeople`)
 * with the HubSpot-specific pipeline + opportunity-status-to-stage mapping. The
 * `statusMapping` shape is derived from `OPPORTUNITY_STATUS_VALUES` so every
 * Forgepoint status stays required as the enum evolves. Each FP status must map
 * to a distinct HubSpot stage so pushed deals can't collide on the same stage.
 */
export const HubSpotSyncConfigSchema = ERPSyncConfigSchema.extend({
  pipelineId: z
    .string()
    .min(1)
    .meta({ title: "Pipeline", description: "HubSpot deal pipeline" }),
  statusMapping: z
    .object(statusMappingShape)
    .optional()
    .meta({
      title: "Opportunity status mapping (legacy)",
      description:
        "Legacy category-based mapping. Kept for backwards compatibility — new configs use stageMapping instead.",
    }),
  stageMapping: z
    .record(z.string(), z.string().min(1))
    .optional()
    .meta({
      title: "Opportunity stage mapping",
      description:
        "Map custom FP opportunity stages to HubSpot deal stages. When set, takes priority over status mapping.",
    }),
  opportunityFieldMapping: OpportunityFieldMappingSchema.meta({
    title: "Opportunity field mapping",
    description:
      "Map Forgepoint opportunity fields to HubSpot deal properties",
  }),
  opportunityCustomFieldMapping: OpportunityCustomFieldMappingSchema.meta({
    title: "Opportunity custom field mapping",
    description:
      "Map Forgepoint opportunity custom fields to HubSpot deal properties",
  }),
});

/**
 * Per-customer HubSpot sync configuration type. Inferred from `HubSpotSyncConfigSchema`.
 */
export type HubSpotSyncConfig = z.infer<typeof HubSpotSyncConfigSchema>;
